import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import toast, { useToaster, toast as hotToast } from 'react-hot-toast';
import type { Toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useExperience } from '../../context/ExperienceContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { supabase } from '../../lib/supabase';
import { getPageHelp } from '../../lib/page-help';
import {
  recordPageVisit,
  getSmartSuggestion,
  markHelpSeen,
  getHelpProgress,
} from '../../lib/daruma-diary';

const DISABLED_KEY = 'tg_daruma_tips_disabled';

function tipKeyForPath(pathname: string): string {
  if (pathname === '/' || pathname === '/dashboard') return 'darumaTips.dashboard';
  if (pathname === '/dream' || pathname.startsWith('/dream?')) return 'darumaTips.dream';
  if (pathname === '/travel') return 'darumaTips.travel';
  if (pathname.startsWith('/trip-planner')) return 'darumaTips.tripPlanner';
  if (pathname.startsWith('/tax-free')) return 'darumaTips.taxFree';
  if (pathname.startsWith('/credits')) return 'darumaTips.credits';
  return 'darumaTips.default';
}

type DarumaPose = 'wave' | 'point' | 'think' | 'explain' | 'celebrate' | 'travel';

function toastPose(type: Toast['type']): DarumaPose {
  switch (type) {
    case 'success': return 'celebrate';
    case 'error':   return 'think';
    case 'loading': return 'think';
    default:        return 'explain';
  }
}

function toastAccent(type: Toast['type']): string {
  switch (type) {
    case 'success': return 'border-l-green-500';
    case 'error':   return 'border-l-red-500';
    default:        return 'border-l-primary-500';
  }
}

interface QuickAction {
  icon: string;
  labelKey: string;
  action: () => void;
}

export function DarumaFab() {
  const { t } = useTranslation('pages');
  const { user } = useAuth();
  const { isManga, isUkiyo, isMangaV3 } = useExperience();
  const { state, replayOnboarding } = useOnboarding();
  const location = useLocation();
  const navigate = useNavigate();

  // Toast queue interception
  const { toasts, handlers } = useToaster();
  const { startPause, endPause } = handlers;
  const activeToast = toasts.find(tt => tt.visible);

  // Popover / FAB state
  const [open, setOpen] = useState(false);
  const [helpMode, setHelpMode] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [disabled, setDisabled] = useState(() => {
    try { return localStorage.getItem(DISABLED_KEY) === '1'; } catch { return false; }
  });

  // Proactive suggestion state — one suggestion per page per session
  const [proactiveDismissed, setProactiveDismissed] = useState<Set<string>>(new Set());
  const [showProactive, setShowProactive] = useState(false);
  const proactiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const eligible = !!user
    && !disabled
    && !location.pathname.startsWith('/admin')
    && state.phase === 'complete';

  const suffix = (isManga || isMangaV3) ? '-manga' : isUkiyo ? '-ukiyo' : '';
  const waveSrc    = `/images/mascot/daruma-wave${suffix}.png`;
  const explainSrc = `/images/mascot/daruma-explain${suffix}.png`;

  // Auto-close popover when a toast arrives so the bubble is the focal point.
  useEffect(() => {
    if (activeToast && open) setOpen(false);
  }, [activeToast, open]);

  // Reset proactive bubble + dismissed set on navigation change. Also
  // record the visit in the companion diary so smart suggestions can
  // adapt over time.
  useEffect(() => {
    recordPageVisit(location.pathname);
    setShowProactive(false);
    if (proactiveTimerRef.current) {
      clearTimeout(proactiveTimerRef.current);
      proactiveTimerRef.current = null;
    }
  }, [location.pathname, location.search]);

  // ── Contextual tip used in the popover ──
  const tip = t(tipKeyForPath(location.pathname));
  const pageHelp = getPageHelp(location.pathname, t);

  // ── Proactive suggestion (DOM-based detection) ──
  interface ProactiveSuggestion {
    pose: DarumaPose;
    messageKey: string;
    actionKey?: string;
    actionRoute?: string;
    actionCallback?: () => void;
    condition: () => boolean;
  }

  function getProactiveSuggestion(): ProactiveSuggestion | null {
    const path = location.pathname;
    const search = location.search || '';
    const pageKey = path + search;
    if (proactiveDismissed.has(pageKey)) return null;

    if (path === '/' || path === '/dashboard') {
      // Tax-free deadline takes priority over everything else on the dashboard.
      const taxFreeAlert = document.querySelector('[data-taxfree-deadline]');
      if (taxFreeAlert) {
        const days = parseInt(taxFreeAlert.getAttribute('data-days') || '999', 10);
        if (days > 0 && days <= 14) {
          return {
            pose: 'explain',
            messageKey: 'daruma.contextTaxFreeDeadline',
            actionKey: 'daruma.contextGoToChecklist',
            actionRoute: '/tax-free/checklist',
            condition: () => true,
          };
        }
      }

      // Trip departure within a week — nudge the user to prep.
      const countdownEl = document.querySelector('[data-trip-countdown]');
      if (countdownEl) {
        const daysAway = parseInt(countdownEl.getAttribute('data-days-away') || '999', 10);
        const tripId = countdownEl.getAttribute('data-trip-id');
        if (tripId && daysAway > 0 && daysAway <= 7) {
          return {
            pose: 'travel',
            messageKey: 'daruma.contextTripSoon',
            actionKey: 'daruma.contextCheckWeather',
            actionRoute: `/trip-planner/${tripId}`,
            condition: () => true,
          };
        }
      }

      // New-user empty dashboard.
      return {
        pose: 'point',
        messageKey: 'daruma.proactiveNewUser',
        actionKey: 'daruma.proactiveExploreAction',
        actionRoute: '/dream',
        condition: () => {
          const tripsBlock = document.querySelector('[data-tour="dashboard-trips"]');
          if (!tripsBlock) return false;
          const tripCards = tripsBlock.querySelectorAll('[role="button"]');
          return tripCards.length === 0;
        },
      };
    }

    if (path === '/dream' && !search.includes('tab=')) {
      return {
        pose: 'explain',
        messageKey: 'daruma.proactiveEmptyMap',
        actionKey: 'daruma.proactiveLibraryAction',
        actionRoute: '/dream?tab=library',
        condition: () => {
          const exploreEl = document.querySelector('[data-explore-locations-count]');
          if (!exploreEl) return false;
          const count = parseInt(exploreEl.getAttribute('data-explore-locations-count') || '0', 10);
          return count === 0;
        },
      };
    }

    if (search.includes('tab=library')) {
      return {
        pose: 'point',
        messageKey: 'daruma.proactiveEmptyLibrary',
        actionKey: 'daruma.proactiveSearchAction',
        actionCallback: () => {
          const wrap = document.querySelector<HTMLElement>('[data-tour="library-search"]');
          const input = wrap?.querySelector<HTMLInputElement>('input');
          (input || wrap)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          input?.focus();
        },
        condition: () => {
          const libEl = document.querySelector('[data-library-locations-count]');
          if (!libEl) return false;
          const count = parseInt(libEl.getAttribute('data-library-locations-count') || '0', 10);
          return count === 0;
        },
      };
    }

    if (path.startsWith('/trip-planner')) {
      // Packed-day check (8+ stops on any single day) takes priority.
      const dayCards = document.querySelectorAll('[data-tour="trip-day"]');
      for (const dayCard of Array.from(dayCards)) {
        const items = dayCard.querySelectorAll('[data-tour="trip-item"], [data-trip-item]');
        if (items.length >= 8) {
          return {
            pose: 'think',
            messageKey: 'daruma.contextPackedDay',
            actionKey: 'daruma.contextAnalyzeAction',
            actionCallback: () => {
              const btn = document.querySelector<HTMLButtonElement>('[data-tour="analyze-trip"]');
              btn?.click();
            },
            condition: () => true,
          };
        }
      }

      // Has hotels but no food at all → suggest planning meals.
      const hotelItems = document.querySelectorAll('[data-hotel-item="true"]');
      const foodItems = document.querySelectorAll('[data-category="restaurant"], [data-category="food"]');
      if (hotelItems.length > 0 && foodItems.length === 0) {
        return {
          pose: 'point',
          messageKey: 'daruma.contextNoFood',
          condition: () => true,
        };
      }

      // Empty day fallback (existing behaviour).
      return {
        pose: 'explain',
        messageKey: 'daruma.proactiveEmptyTrip',
        actionKey: 'daruma.proactiveAddLocationAction',
        actionCallback: () => {
          const btn = document.querySelector<HTMLButtonElement>('[data-tour="add-location"]');
          btn?.click();
        },
        condition: () => {
          // Sum item counts across every day card. data-tour="trip-item" only
          // marks index 0 of each day for tour purposes, and collapsed days
          // don't render any items at all — so don't rely on it. The day card
          // exposes data-day-item-count which reflects the underlying state.
          const days = document.querySelectorAll('[data-day-item-count]');
          let total = 0;
          for (const el of Array.from(days)) {
            total += parseInt(el.getAttribute('data-day-item-count') || '0', 10) || 0;
          }
          // Only fire on a genuinely empty trip — at least one day with at
          // least one item silences the nudge.
          return days.length > 0 && total === 0;
        },
      };
    }

    // Diary-driven fallback — long-running behavioural nudges.
    const smart = getSmartSuggestion();
    if (smart) {
      return {
        pose: smart.pose,
        messageKey: smart.messageKey,
        actionRoute: smart.actionRoute,
        condition: () => true,
      };
    }

    return null;
  }

  // Schedule the proactive check — fires 10s after a navigation, only if
  // nothing else is competing for the user's attention.
  useEffect(() => {
    if (!eligible || open || activeToast || state.phase !== 'complete') return;
    const candidate = getProactiveSuggestion();
    if (!candidate) return;

    proactiveTimerRef.current = setTimeout(() => {
      const fresh = getProactiveSuggestion();
      if (fresh && fresh.condition()) {
        setShowProactive(true);
      }
    }, 10000);

    return () => {
      if (proactiveTimerRef.current) {
        clearTimeout(proactiveTimerRef.current);
        proactiveTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, eligible, open, !!activeToast]);

  function dismissProactive() {
    setShowProactive(false);
    setProactiveDismissed(prev => new Set(prev).add(location.pathname + (location.search || '')));
  }

  // Defer focus past the popover-unmount tick so React doesn't steal it
  // back, and flash a brief ring so the user sees where the cursor went.
  function focusSearchTarget(tourMarker: string) {
    const tryFocus = () => {
      const wrap = document.querySelector<HTMLElement>(`[data-tour="${tourMarker}"]`);
      const input = wrap?.querySelector<HTMLInputElement>('input');
      const target = input || wrap;
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input?.focus();
      if (target instanceof HTMLElement) {
        target.classList.add('ring-2', 'ring-primary-500', 'rounded-xl');
        window.setTimeout(() => {
          target.classList.remove('ring-2', 'ring-primary-500', 'rounded-xl');
        }, 1500);
      }
    };
    // Two RAFs: one for popover unmount commit, one for the layout pass.
    requestAnimationFrame(() => requestAnimationFrame(tryFocus));
  }

  // ── Quick actions ──
  function getQuickActions(): QuickAction[] {
    const path = location.pathname;
    const search = location.search || '';

    if (path === '/' || path === '/dashboard') {
      return [
        { icon: '📍', labelKey: 'daruma.actionClaimSpot', action: () => {
          const btn = document.querySelector<HTMLButtonElement>('[data-tour="dashboard-weekly-spot"] button, [data-action="claim-weekly"]');
          if (btn) btn.click(); else navigate('/dream?tab=passport');
        }},
        { icon: '✈️', labelKey: 'daruma.actionCreateTrip', action: () => navigate('/travel') },
      ];
    }
    if (path === '/dream' && !search.includes('tab=')) {
      return [
        { icon: '📚', labelKey: 'daruma.actionGoToLibrary', action: () => navigate('/dream?tab=library') },
        { icon: '🔍', labelKey: 'daruma.actionSearchLocations', action: () => focusSearchTarget('explore-search') },
      ];
    }
    if (search.includes('tab=library')) {
      return [
        // navigate() is more reliable than HTMLAnchorElement.click() on a
        // React Router <Link>, which depends on the synthetic onClick chain.
        { icon: '➕', labelKey: 'daruma.actionAddCustom', action: () => navigate('/locations/new') },
      ];
    }
    if (path.startsWith('/trip-planner')) {
      return [
        { icon: '➕', labelKey: 'daruma.actionAddLocation', action: () => {
          // Sidebar "Search locations" or empty-day "Add activity" button.
          const btn = document.querySelector<HTMLButtonElement>('[data-tour="add-location"]');
          if (btn && !btn.disabled) btn.click();
        }},
        { icon: '🤖', labelKey: 'daruma.actionAnalyze', action: () => {
          const btn = document.querySelector<HTMLButtonElement>('[data-tour="analyze-trip"]');
          if (btn && !btn.disabled) btn.click();
        }},
      ];
    }
    if (path.startsWith('/tax-free')) {
      return [
        { icon: '📊', labelKey: 'daruma.actionGoToTracker', action: () => navigate('/tax-free/tracker') },
      ];
    }
    return [];
  }

  const quickActions = getQuickActions();

  // ── Replay onboarding from the FAB ──
  async function handleReplayFullOnboarding() {
    if (!user) return;
    try {
      await replayOnboarding();
      try {
        await supabase.from('user_tutorial_progress').upsert({
          user_id: user.id,
          has_completed_onboarding: false,
        }, { onConflict: 'user_id' });
      } catch { /* non-fatal */ }
      toast.success(t('profile.replayDarumaTourConfirm'));
      setOpen(false);
      setHelpMode(false);
      navigate('/');
    } catch (err) {
      toast.error((err as Error).message || 'Failed');
    }
  }

  function handleDisable() {
    try { localStorage.setItem(DISABLED_KEY, '1'); } catch { /* ignore */ }
    setDisabled(true);
    setOpen(false);
  }

  if (!eligible) return null;

  // FAB pose mirrors the active toast so the mascot reacts to events.
  const fabSrc = activeToast
    ? `/images/mascot/daruma-${toastPose(activeToast.type)}${suffix}.png`
    : waveSrc;

  // The proactive bubble's currently-active suggestion (recomputed on each
  // render — cheap, and means the user always sees the right pose).
  const proactiveSuggestion = showProactive && !activeToast && !open
    ? getProactiveSuggestion()
    : null;

  return (
    <>
      {/* ── Toast speech bubble ── */}
      {activeToast && !open && (
        <div
          className={`fixed bottom-[72px] right-4 z-[1001] max-w-[320px] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 border-l-4 ${toastAccent(activeToast.type)} p-3`}
          onMouseEnter={() => startPause()}
          onMouseLeave={() => endPause()}
          style={{ animation: 'darumaToastIn 0.3s ease-out' }}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-2">
            <img
              src={`/images/mascot/daruma-${toastPose(activeToast.type)}${suffix}.png`}
              alt=""
              className="w-8 h-8 object-contain flex-shrink-0"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="flex-1 min-w-0 text-sm text-gray-900 dark:text-white leading-snug break-words">
              {typeof activeToast.message === 'function' ? activeToast.message(activeToast) : activeToast.message}
            </div>
            <button
              onClick={() => hotToast.dismiss(activeToast.id)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
          <div className="absolute -bottom-2 right-6 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-white dark:border-t-gray-800" />
        </div>
      )}

      {/* ── Proactive suggestion bubble ── */}
      {proactiveSuggestion && (
        <div
          className="fixed bottom-[72px] right-4 z-[1001] max-w-[320px] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 border-l-4 border-l-primary-500 p-3"
          style={{ animation: 'darumaToastIn 0.3s ease-out' }}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-2">
            <img
              src={`/images/mascot/daruma-${proactiveSuggestion.pose}${suffix}.png`}
              alt=""
              className="w-8 h-8 object-contain flex-shrink-0"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 dark:text-white leading-snug">
                {t(proactiveSuggestion.messageKey)}
              </p>
              {proactiveSuggestion.actionKey && (
                <button
                  onClick={() => {
                    if (proactiveSuggestion.actionRoute) navigate(proactiveSuggestion.actionRoute);
                    if (proactiveSuggestion.actionCallback) proactiveSuggestion.actionCallback();
                    dismissProactive();
                  }}
                  className="mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t(proactiveSuggestion.actionKey)} →
                </button>
              )}
            </div>
            <button
              onClick={dismissProactive}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
          <div className="absolute -bottom-2 right-6 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-white dark:border-t-gray-800" />
        </div>
      )}

      {/* ── Default popover ── */}
      {open && !helpMode && (
        <>
          <div className="fixed inset-0 z-[999]" onClick={() => setOpen(false)} aria-hidden />
          <div className="fixed bottom-20 right-4 z-[1001] w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Daruma-kun</span>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close">
                <X size={14} />
              </button>
            </div>

            <div className="px-4 pb-3">
              <div className="flex items-start gap-2">
                {imgFailed ? (
                  <div className="w-8 h-8 rounded-full bg-error-500 flex items-center justify-center text-white text-base flex-shrink-0" aria-hidden>🎯</div>
                ) : (
                  <img
                    src={explainSrc}
                    alt="Daruma-kun"
                    className="w-8 h-8 object-contain flex-shrink-0"
                    onError={() => setImgFailed(true)}
                  />
                )}
                <p className="text-sm text-gray-800 dark:text-gray-100 leading-snug">{tip}</p>
              </div>
            </div>

            {quickActions.length > 0 && (
              <div className="px-4 pb-3 flex flex-wrap gap-2">
                {quickActions.map(qa => (
                  <button
                    key={qa.labelKey}
                    onClick={() => { qa.action(); setOpen(false); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                  >
                    <span aria-hidden>{qa.icon}</span> {t(qa.labelKey)}
                  </button>
                ))}
              </div>
            )}

            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 space-y-1.5">
              {pageHelp && (
                <button
                  onClick={() => {
                    const basePath = '/' + (location.pathname.split('/')[1] || '');
                    markHelpSeen(basePath);
                    setHelpMode(true);
                  }}
                  className="flex items-center gap-2 w-full text-left text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700"
                >
                  🔍 {t('darumaTips.helpThisPage')}
                </button>
              )}
              <button
                onClick={handleReplayFullOnboarding}
                className="flex items-center gap-2 w-full text-left text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700"
              >
                🎯 {t('profile.replayDarumaTour')}
              </button>
              <Link to="/help" onClick={() => setOpen(false)} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700">
                📖 {t('darumaTips.fullGuide')}
              </Link>
              <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer pt-1">
                <input type="checkbox" onChange={handleDisable} className="rounded" />
                {t('darumaTips.disableTips')}
              </label>
            </div>
          </div>
        </>
      )}

      {/* ── Help mode (expanded per-page tips) ── */}
      {open && helpMode && pageHelp && (
        <>
          <div className="fixed inset-0 z-[999]" onClick={() => { setOpen(false); setHelpMode(false); }} aria-hidden />
          <div className="fixed bottom-20 right-4 z-[1001] w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
              <button onClick={() => setHelpMode(false)} className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700">
                ← {t('darumaTips.backToTips')}
              </button>
              <button onClick={() => { setOpen(false); setHelpMode(false); }} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <X size={14} />
              </button>
            </div>
            <div className="px-4 pb-2 shrink-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{pageHelp.title}</h3>
            </div>
            {(() => {
              const progress = getHelpProgress();
              return (
                <div className="px-4 pb-2 shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all duration-500"
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {t('daruma.helpProgress', { seen: progress.seen, total: progress.total })}
                    </span>
                  </div>
                  {progress.percentage === 100 && (
                    <p className="text-[10px] text-primary-600 dark:text-primary-400 mt-1">
                      {t('daruma.helpComplete')}
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="flex-1 overflow-y-auto px-4 pb-3 space-y-3 min-h-0">
              {pageHelp.tips.map((tip, i) => {
                const poses: DarumaPose[] = ['celebrate', 'point', 'explain', 'travel'];
                const pose = poses[i % poses.length];
                return (
                  <div key={tip.label} className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <img
                      src={`/images/mascot/daruma-${pose}${suffix}.png`}
                      alt=""
                      className="w-7 h-7 object-contain flex-shrink-0"
                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white">{tip.label}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mt-0.5">{tip.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 shrink-0 flex gap-3">
              <Link to="/help" onClick={() => { setOpen(false); setHelpMode(false); }} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
                📖 {t('darumaTips.fullGuide')}
              </Link>
              <Link to="/faq" onClick={() => { setOpen(false); setHelpMode(false); }} className="text-xs text-gray-500 dark:text-gray-400 hover:underline">
                ❓ FAQ
              </Link>
            </div>
          </div>
        </>
      )}

      {/* ── FAB button ── */}
      <button
        type="button"
        onClick={() => {
          if (activeToast) hotToast.dismiss(activeToast.id);
          if (showProactive) dismissProactive();
          setHelpMode(false);
          setOpen(o => !o);
        }}
        className="fixed bottom-4 right-4 z-[1000] w-12 h-12 rounded-full bg-white dark:bg-gray-800 border-2 border-primary-500 shadow-md overflow-hidden hover:scale-105 transition-transform"
        aria-label="Daruma-kun"
      >
        {imgFailed ? (
          <span className="flex items-center justify-center w-full h-full text-2xl" aria-hidden>🎯</span>
        ) : (
          <img
            src={fabSrc}
            alt="Daruma-kun"
            className="w-full h-full object-cover object-top"
            onError={() => setImgFailed(true)}
          />
        )}
      </button>
    </>
  );
}
